/**
 * Case-approvals detail panel — the expandable review surface for a single
 * submitted case: full metadata, quote builder, approve/deny actions, and
 * the contract + payment readouts once approved. A "View all" button opens
 * the full-case review/edit modal (Fix 21). Extracted from the former
 * case-approvals.tsx monolith; theme-driven via the shared admin/ui kit.
 */
import { useEffect, useMemo, useState } from "react";
import { Text, View } from "react-native";

import {
  approveCase,
  beginCaseReview,
  computeQuotePreview,
  denyCase,
  denyCaseTerminal,
  formatCents,
  getCaseForReview,
  sendContractForCase,
  type CaseForReview,
  type PendingCase,
  type QuotePreview,
} from "../../../lib/caseApprovals";
import type { CompetitionTier, PricingTier } from "../../../types/database";
import { validatePromoCode, type PromoValidation } from "../../../lib/promos";
import { RADII, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
} from "../ui";
import { DetailRow, PreviewRow, SubLabel, TierPicker } from "./parts";
import { ContractSection, PaymentSection } from "./sections";
import { CaseReviewModal } from "./CaseReviewModal";
import { engagementLabel, formatFullTimestamp } from "./helpers";

const THEME = PANE_THEMES.Admin;

type ActionKind = "approve" | "deny" | "deny_terminal";

const panelStyle = {
  marginTop: 8,
  padding: 16,
  borderRadius: RADII.md,
  backgroundColor: THEME.pageBg,
  borderWidth: STROKES.hairline,
  borderColor: THEME.border,
} as const;

const innerCardStyle = {
  marginTop: 6,
  padding: 12,
  borderRadius: RADII.sm,
  borderWidth: STROKES.hairline,
  borderColor: THEME.border,
  backgroundColor: THEME.surfaceRaisedBg,
  gap: 4,
} as const;

export function CaseDetailPanel({
  competitionId,
  isSuperAdmin,
  pricing,
  onClose,
  onActionComplete,
  onDecided,
}: {
  competitionId: string;
  isSuperAdmin: boolean;
  pricing: PricingTier[];
  onClose: () => void;
  onActionComplete: () => void;
  /** Refresh parent lists WITHOUT closing the panel (post approve+send). */
  onDecided: () => void;
}) {
  const [detail, setDetail] = useState<CaseForReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const [selectedTier, setSelectedTier] = useState<CompetitionTier | null>(null);
  const [scholarshipDollars, setScholarshipDollars] = useState("0");
  const [manualDiscountPct, setManualDiscountPct] = useState("0");
  const [notes, setNotes] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promoValidation, setPromoValidation] = useState<PromoValidation | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);

  const [denying, setDenying] = useState<null | "deny" | "deny_terminal">(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await getCaseForReview(competitionId);
        if (cancelled) return;
        setDetail(d);
        if (d) {
          setSelectedTier((d.tier as CompetitionTier | null) ?? null);
          const prizeTotalCents = (d.prize_structure ?? []).reduce(
            (sum, p) => sum + (typeof p?.amount_cents === "number" ? p.amount_cents : 0),
            0
          );
          setScholarshipDollars(prizeTotalCents > 0 ? String(prizeTotalCents / 100) : "0");
          const pending = (d as { pending_promo_code?: string | null }).pending_promo_code ?? null;
          if (pending && !cancelled) {
            setPromoInput(pending);
            try {
              const v = await validatePromoCode(pending, competitionId);
              if (!cancelled) setPromoValidation(v);
            } catch {
              // Swallow — surfaced via promoValidation.
            }
          }
        }
        if (d?.status === "submitted") {
          try {
            await beginCaseReview(competitionId);
            const fresh = await getCaseForReview(competitionId);
            if (!cancelled && fresh) setDetail(fresh);
          } catch {
            // Soft-claim is best-effort.
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load case");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  const scholarshipCents = useMemo(() => {
    const n = parseFloat(scholarshipDollars);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 100);
  }, [scholarshipDollars]);

  const manualPct = useMemo(() => {
    const n = parseInt(manualDiscountPct, 10);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(100, n);
  }, [manualDiscountPct]);

  const preview: QuotePreview | null = useMemo(() => {
    if (!detail) return null;
    return computeQuotePreview({
      tier: selectedTier,
      engagement: detail.engagement_model as "licensing" | "rm" | "hybrid",
      scholarshipCents,
      manualDiscountPercent: isSuperAdmin ? manualPct : 0,
      pricing,
    });
  }, [detail, selectedTier, scholarshipCents, manualPct, isSuperAdmin, pricing]);

  async function refreshDetail() {
    const fresh = await getCaseForReview(competitionId);
    if (fresh) setDetail(fresh);
  }

  async function runAction(kind: ActionKind) {
    if (!detail) return;
    setActionErr(null);
    setBusy(true);
    try {
      if (kind === "approve") {
        const trimmedPromo = promoInput.trim().toUpperCase();
        const promoToApply = trimmedPromo.length > 0 ? trimmedPromo : null;
        await approveCase({
          competitionId,
          manualDiscountPercent: isSuperAdmin ? manualPct : 0,
          scholarshipAmountCents: scholarshipCents,
          notesForClient: notes.trim() || null,
          tier: selectedTier,
          promoCode: promoToApply,
        });
        // Approve landed; auto-send the DocuSign envelope (one click does both).
        setSendingContract(true);
        try {
          await sendContractForCase(competitionId);
        } catch (sendErr) {
          await refreshDetail();
          setActionErr(
            `Case approved and quote created, but sending the DocuSign contract failed: ${
              sendErr instanceof Error ? sendErr.message : "unknown error"
            } — retry with "Send contract" in the Contract section below.`,
          );
          return;
        }
        await refreshDetail();
        onDecided();
        return;
      } else if (kind === "deny") {
        await denyCase(competitionId, reason.trim());
      } else {
        await denyCaseTerminal(competitionId, reason.trim());
      }
      onActionComplete();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : "Action failed");
    } finally {
      setSendingContract(false);
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <View style={panelStyle}>
        <AdminLoading theme={THEME} />
      </View>
    );
  }
  if (err || !detail) {
    return (
      <View style={panelStyle}>
        <AdminError theme={THEME} message={err ?? "Case not found"} />
        <View style={{ marginTop: 12 }}>
          <AdminButton theme={THEME} label="Close" variant="outline" size="sm" onPress={onClose} />
        </View>
      </View>
    );
  }

  const isReviewable = detail.status === "submitted" || detail.status === "under_review";

  return (
    <View style={panelStyle}>
      {/* View-all entry point (Fix 21) */}
      <View style={{ flexDirection: "row", justifyContent: "flex-end", marginBottom: 4 }}>
        <AdminButton theme={THEME} label="View all details" variant="outline" size="sm" onPress={() => setReviewOpen(true)} />
      </View>

      {/* Case metadata */}
      <SubLabel>Case</SubLabel>
      <DetailRow k="Name" v={detail.name} />
      <DetailRow k="Company" v={detail.company_name ?? "—"} />
      <DetailRow k="Client" v={detail.client_full_name ?? "—"} />
      <DetailRow k="Client email" v={detail.client_email ?? "—"} />
      <DetailRow k="Engagement" v={engagementLabel(detail.engagement_model as PendingCase["engagement_model"])} />
      <DetailRow k="Tier" v={detail.tier ?? "Not set"} />
      <DetailRow k="Status" v={detail.status} />
      <DetailRow k="Submitted" v={formatFullTimestamp(detail.submitted_at)} />
      <DetailRow k="Review started" v={formatFullTimestamp(detail.review_started_at)} />

      {detail.brief ? (
        <>
          <SubLabel style={{ marginTop: 14 }}>Brief</SubLabel>
          <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurface }}>
            {detail.brief}
          </Text>
        </>
      ) : null}

      {detail.deliverable_description ? (
        <>
          <SubLabel style={{ marginTop: 14 }}>Deliverables</SubLabel>
          <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurfaceDim }}>
            {detail.deliverable_description}
          </Text>
        </>
      ) : null}

      {/* Quote builder */}
      {isReviewable && !denying ? (
        <>
          <SubLabel style={{ marginTop: 18 }}>Quote builder</SubLabel>

          <TierPicker
            engagement={detail.engagement_model as "licensing" | "rm" | "hybrid"}
            pricing={pricing}
            selected={selectedTier}
            onSelect={setSelectedTier}
          />

          <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
            <View style={{ flexBasis: 200, flexGrow: 1 }}>
              <AdminInput theme={THEME} label="Scholarship ($)" value={scholarshipDollars} onChangeText={setScholarshipDollars} placeholder="0.00" keyboardType="numeric" />
            </View>
            <View style={{ flexBasis: 200, flexGrow: 1 }}>
              <AdminInput
                theme={THEME}
                label={`Manual discount (%)${isSuperAdmin ? "" : " · super_admin only"}`}
                value={manualDiscountPct}
                onChangeText={setManualDiscountPct}
                placeholder="0"
                keyboardType="numeric"
                editable={isSuperAdmin}
              />
            </View>
          </View>

          <SubLabel style={{ marginTop: 14 }}>Promo code (optional)</SubLabel>
          <View style={{ marginTop: 6, flexDirection: "row", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <View style={{ flex: 1, minWidth: 200 }}>
              <AdminInput
                theme={THEME}
                value={promoInput}
                onChangeText={(v) => {
                  setPromoInput(v.toUpperCase());
                  setPromoValidation(null);
                }}
                placeholder="PROMO CODE"
                autoCapitalize="characters"
              />
            </View>
            <AdminButton
              theme={THEME}
              label="Validate"
              variant="outline"
              size="sm"
              loading={promoChecking}
              disabled={!promoInput.trim()}
              onPress={async () => {
                if (!promoInput.trim()) return;
                setPromoChecking(true);
                try {
                  setPromoValidation(await validatePromoCode(promoInput.trim(), competitionId));
                } finally {
                  setPromoChecking(false);
                }
              }}
            />
            {promoInput ? (
              <AdminButton theme={THEME} label="Clear" variant="ghost" size="sm" onPress={() => { setPromoInput(""); setPromoValidation(null); }} />
            ) : null}
          </View>
          {promoValidation ? (
            promoValidation.valid ? (
              <View style={{ marginTop: 8 }}>
                <AdminBanner
                  theme={THEME}
                  tone="success"
                  message={`✓ ${promoValidation.discount_label}${promoValidation.discount_cents > 0 ? ` — ${formatCents(promoValidation.discount_cents)} off` : ""}`}
                />
              </View>
            ) : (
              <Text style={{ marginTop: 8, fontFamily: THEME.bodyMediumFont, fontSize: 11.5, color: THEME.destructive }}>
                {promoValidation.reason || "Code is not valid"}
              </Text>
            )
          ) : null}

          <View style={{ marginTop: 14 }}>
            <AdminInput
              theme={THEME}
              label="Notes for client (optional)"
              value={notes}
              onChangeText={setNotes}
              multiline
              minHeight={60}
              placeholder="Anything you want surfaced in the approval email."
            />
          </View>

          <SubLabel style={{ marginTop: 14 }}>Quote preview</SubLabel>
          {!selectedTier ? (
            <View style={{ marginTop: 6, padding: 12, borderRadius: RADII.sm, borderWidth: STROKES.hairline, borderColor: THEME.border, backgroundColor: THEME.altBg }}>
              <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
                Pick a tier above to see the line items.
              </Text>
            </View>
          ) : preview?.error ? (
            <View style={{ marginTop: 6 }}>
              <AdminBanner theme={THEME} tone="error" message={preview.error} />
            </View>
          ) : preview ? (
            <View style={innerCardStyle}>
              <PreviewRow k="Per-student rate" v={formatCents(preview.perStudentCents)} />
              <PreviewRow k="Per-submission rate" v={formatCents(preview.perSubmissionCents)} />
              {preview.perPresentationCents != null ? <PreviewRow k="Per-presentation rate" v={formatCents(preview.perPresentationCents)} /> : null}
              {preview.flatRmCents != null ? <PreviewRow k="Flat RM fee" v={formatCents(preview.flatRmCents)} /> : null}
              <PreviewRow k="Total discount applied" v={preview.totalDiscountPercent > 0 ? `${preview.totalDiscountPercent}%` : "—"} />
              <View style={{ marginTop: 4, paddingTop: 8, borderTopWidth: STROKES.hairline, borderTopColor: THEME.border }}>
                <PreviewRow k="Initial payment due" v={formatCents(preview.initialPaymentCents)} strong />
                {promoValidation?.valid && promoValidation.discount_cents > 0 ? (
                  <>
                    <PreviewRow k={`Promo: ${promoInput.trim().toUpperCase()}`} v={`−${formatCents(promoValidation.discount_cents)}`} />
                    <PreviewRow k="After promo" v={formatCents(Math.max(0, preview.initialPaymentCents - promoValidation.discount_cents))} strong />
                  </>
                ) : null}
              </View>
            </View>
          ) : null}

          {actionErr ? <View style={{ marginTop: 10 }}><AdminError theme={THEME} message={actionErr} /></View> : null}

          <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <AdminButton
              theme={THEME}
              label={sendingContract ? "Sending contract…" : "Approve & send contract"}
              loading={busy}
              disabled={!selectedTier || !!preview?.error}
              onPress={() => void runAction("approve")}
            />
            <AdminButton theme={THEME} label="Deny with revision allowed" variant="outline" disabled={busy} onPress={() => { setReason(""); setDenying("deny"); }} />
            <AdminButton theme={THEME} label="Deny — no resubmissions" variant="destructive" disabled={busy} onPress={() => { setReason(""); setDenying("deny_terminal"); }} />
            <AdminButton theme={THEME} label="Close" variant="ghost" disabled={busy} onPress={onClose} />
          </View>
        </>
      ) : null}

      {/* Denial reason inline */}
      {denying ? (
        <View style={{ marginTop: 18 }}>
          <SubLabel>
            {denying === "deny_terminal" ? "Reason — terminal denial (no resubmissions)" : "Reason — revision allowed"}
          </SubLabel>
          <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 11.5, color: denying === "deny_terminal" ? THEME.destructive : THEME.textOnSurfaceDim }}>
            {denying === "deny_terminal"
              ? "The client will be told their case is permanently denied. They cannot revise + resubmit. Reserve this for cases that violate Coravu policy."
              : "The client will see this reason in their case editor and can revise + resubmit."}
          </Text>
          <View style={{ marginTop: 8 }}>
            <AdminInput
              theme={THEME}
              value={reason}
              onChangeText={setReason}
              multiline
              minHeight={90}
              placeholder="Why this case was denied. The client will see this verbatim."
            />
          </View>
          {actionErr ? <View style={{ marginTop: 10 }}><AdminError theme={THEME} message={actionErr} /></View> : null}
          <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <AdminButton
              theme={THEME}
              label={denying === "deny_terminal" ? "Confirm terminal denial" : "Confirm denial"}
              variant={denying === "deny_terminal" ? "destructive" : "filled"}
              loading={busy}
              disabled={reason.trim().length === 0}
              onPress={() => void runAction(denying)}
            />
            <AdminButton theme={THEME} label="Cancel" variant="ghost" disabled={busy} onPress={() => { setDenying(null); setReason(""); setActionErr(null); }} />
          </View>
        </View>
      ) : null}

      {/* Past quote (non-pending states) */}
      {detail.latest_quote && !isReviewable ? (
        <View style={{ marginTop: 18 }}>
          <SubLabel>Latest quote</SubLabel>
          <View style={innerCardStyle}>
            <PreviewRow k="Status" v={detail.latest_quote.status} />
            <PreviewRow k="Tier" v={detail.latest_quote.tier} />
            <PreviewRow k="Scholarship" v={formatCents(detail.latest_quote.scholarship_amount_cents)} />
            <PreviewRow k="Manual discount" v={`${detail.latest_quote.manual_discount_percent}%`} />
            <PreviewRow k="Per-student rate" v={formatCents(detail.latest_quote.per_student_rate_cents)} />
            <PreviewRow k="Per-submission rate" v={formatCents(detail.latest_quote.per_submission_rate_cents)} />
            {detail.latest_quote.flat_rm_fee_cents != null ? <PreviewRow k="Flat RM fee" v={formatCents(detail.latest_quote.flat_rm_fee_cents)} /> : null}
            <PreviewRow k="Initial payment" v={formatCents(detail.latest_quote.initial_payment_cents)} strong />
          </View>
        </View>
      ) : null}

      {/* Contract */}
      {detail.status === "approved" ||
      detail.status === "contract_pending" ||
      detail.status === "contract_signed" ||
      detail.status === "payment_pending" ||
      detail.status === "final_invoice_pending" ||
      detail.status === "final_invoice_paid" ? (
        <>
          {actionErr ? <View style={{ marginTop: 10 }}><AdminError theme={THEME} message={actionErr} /></View> : null}
          <ContractSection
            competitionId={competitionId}
            caseStatus={detail.status}
            hasQuote={!!detail.latest_quote}
            onContractSent={() => {
              setActionErr(null);
              onDecided();
              void refreshDetail();
            }}
          />
        </>
      ) : null}

      {/* Initial payment */}
      {detail.status === "contract_signed" || detail.status === "payment_pending" ? (
        <PaymentSection competitionId={competitionId} />
      ) : null}

      {/* Denial reason */}
      {(detail.status === "denied" || detail.status === "denied_terminal") && detail.denial_reason ? (
        <View style={{ marginTop: 18 }}>
          <SubLabel>Denial reason</SubLabel>
          <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurface }}>
            {detail.denial_reason}
          </Text>
        </View>
      ) : null}

      {reviewOpen ? (
        <CaseReviewModal
          detail={detail}
          onClose={() => setReviewOpen(false)}
          onSaved={() => void refreshDetail()}
        />
      ) : null}
    </View>
  );
}
